import React, { useState, useEffect } from "react";
import { ArrowRight, ChevronDown, Check } from "lucide-react";
import { contact } from "../mock";
import { Reveal } from "../hooks/useReveal";

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", interest: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 15) e.phone = "Enter a valid phone number";
    if (!EMAIL_RE.test(form.email)) e.email = "Enter a valid email address";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent, source: "contact" }),
      });
      if (!res.ok) throw new Error("failed");
      setSent(true);
    } catch (err) {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls = "font-sans text-[10px] uppercase tracking-[0.3em] text-[#F1E6D7]/55";
  const inputBase =
    "mt-3 w-full border-b bg-transparent py-2.5 text-[15px] font-light text-white placeholder-white/40 transition-colors focus:outline-none";
  const borderCls = (k) => (errors[k] ? "border-red-400/70" : "border-white/25 focus:border-bronze-light");

  return (
    <section
      id="contact"
      className="flex min-h-screen items-center bg-[#221810] py-28 font-sans text-[#F1E6D7] antialiased"
    >
      <div className="mx-auto w-full max-w-xl px-6">
        <Reveal>
          <p className="text-center font-sans text-[11px] uppercase tracking-[0.3em] text-bronze-light">
            {contact.kicker}
          </p>
          <h2 className="mt-6 text-center font-sans text-3xl font-light uppercase tracking-[0.25em] sm:text-5xl">
            {contact.title}
          </h2>
          <p className="mx-auto mt-6 max-w-md text-center text-[14px] font-light leading-[1.9] text-[#F1E6D7]/70">
            {contact.subtitle}
          </p>
        </Reveal>

        {sent ? (
          <Reveal>
            <div className="mt-14 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-bronze-light">
                <Check size={28} className="text-bronze-light" />
              </div>
              <h3 className="font-sans text-xl font-light uppercase tracking-[0.25em]">Thank You</h3>
              <p className="mt-3 text-[14px] font-light text-[#F1E6D7]/70">
                Your inquiry has been received. James will personally follow up shortly.
              </p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={120}>
            <form onSubmit={submit} noValidate className="mt-14 space-y-8">
              <div>
                <label className={labelCls}>Full Name *</label>
                <input type="text" placeholder="Enter your full name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={`${inputBase} ${borderCls("name")}`} />
                {errors.name && <p className="mt-1.5 text-[10px] tracking-[0.1em] text-red-300">{errors.name}</p>}
              </div>
              <div>
                <label className={labelCls}>Phone *</label>
                <input type="tel" placeholder="Enter your phone number" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={`${inputBase} ${borderCls("phone")}`} />
                {errors.phone && <p className="mt-1.5 text-[10px] tracking-[0.1em] text-red-300">{errors.phone}</p>}
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" placeholder="Enter your email address" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={`${inputBase} ${borderCls("email")}`} />
                {errors.email && <p className="mt-1.5 text-[10px] tracking-[0.1em] text-red-300">{errors.email}</p>}
              </div>
              <div>
                <label className={labelCls}>Interested In</label>
                <div className="relative">
                  <select value={form.interest}
                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                    className={`${inputBase} ${borderCls("interest")} appearance-none pr-8 ${form.interest ? "text-white" : "text-white/40"}`}>
                    <option value="" className="text-ink">Select one</option>
                    {contact.interests.map((o) => (
                      <option key={o} value={o} className="text-ink">{o}</option>
                    ))}
                  </select>
                  <ChevronDown size={16} className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-bronze-light" />
                </div>
              </div>

              <label className="flex cursor-pointer items-start gap-3">
                <span onClick={() => setConsent(!consent)}
                  className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center border transition-colors ${
                    consent ? "border-bronze-light bg-bronze-light" : "border-white/40"
                  }`}>
                  {consent && <Check size={12} className="text-ink" />}
                </span>
                <span className="text-[11px] font-light leading-relaxed text-[#F1E6D7]/55">{contact.consent}</span>
              </label>

              {errors.submit && <p className="text-[11px] tracking-[0.1em] text-red-300">{errors.submit}</p>}

              <button type="submit" disabled={submitting}
                className="group flex w-full items-center justify-center gap-3 bg-[#F1E6D7] py-4 font-sans text-[11px] uppercase tracking-[0.3em] text-ink transition-all duration-500 hover:bg-white disabled:opacity-60">
                {submitting ? "Sending..." : contact.cta}
                {!submitting && <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
